import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import ReactDOM from "react-dom"
import { useHistory } from "react-router-dom"

import * as styles from "../styles"

// Portal-based interactive tooltip — escapes overflow:hidden ancestors (BatchBox
// CardContent, RowContainer) and stays open while the cursor is inside it,
// so its content (e.g. links) can be clicked. Visual style follows Material
// Design's dark-tooltip recommendation: bg #424952 (≈ Material grey-700) — NOT
// pure #000, which makes white text render thin/fuzzy. 13px @ 500 weight is the
// typical crisp-on-dark size; default font-smoothing (no override).

const TOOLTIP_MAX_WIDTH = 280
const TOOLTIP_OFFSET_Y = 4
const TOOLTIP_HIDE_DELAY_MS = 300

const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\(([^)]+)\)/g

// Module-level singleton: only one tooltip is ever open at a time. When a
// new Tooltip's trigger receives mouseenter, it hides the previously-active
// one before showing itself. Avoids the "tooltips pile up while moving
// between nested triggers" issue. Identity uses a per-instance ref token
// (not the hide function itself, which is recreated each render).
let activeId: object | null = null
let activeHide: (() => void) | null = null

// Renders a string that may contain markdown-style inline links — `[label](url)`.
// Relative URLs (starting with `?` or `/`) navigate via `history.push`; absolute
// URLs open in a new tab. Anchors use the dark-tooltip-aware TooltipLink style.
export const renderWithLinks = (
  text: string,
  history: ReturnType<typeof useHistory>,
): React.ReactNode => {
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  MARKDOWN_LINK_REGEX.lastIndex = 0
  while ((match = MARKDOWN_LINK_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    const [, label, url] = match
    const isRelative = url.startsWith("?") || url.startsWith("/")
    parts.push(
      <styles.TooltipLink
        key={match.index}
        href={url}
        onClick={(event) => {
          event.stopPropagation()
          if (isRelative) {
            event.preventDefault()
            if (url.startsWith("?")) {
              history.push({ search: url })
            } else {
              history.push(url)
            }
          }
        }}
        target={isRelative ? undefined : "_blank"}
        rel={isRelative ? undefined : "noopener noreferrer"}
      >
        {label}
      </styles.TooltipLink>,
    )
    lastIndex = MARKDOWN_LINK_REGEX.lastIndex
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }
  return <>{parts}</>
}

type TooltipProps = {
  content: React.ReactNode
  // When true, the trigger wrapper is a block-level div instead of the
  // default inline-flex span. Needed when wrapping block-level elements
  // (e.g. a click-halo around a multi-line quote) — inline-flex would
  // collapse the child's layout.
  block?: boolean
  // Default true: tooltip stays open for ~300ms after the cursor leaves
  // the trigger so the user can move into the tooltip to click links
  // inside it. Set false for informational tooltips that should disappear
  // immediately on leave or click.
  interactive?: boolean
  showDelayMs?: number // hover time before the tooltip opens; 0 opens it at once
}

const Tooltip: React.FC<React.PropsWithChildren<TooltipProps>> = ({
  content,
  block = false,
  interactive = true,
  showDelayMs = 0,
  children,
}) => {
  const [show, setShow] = useState(false)
  // `placed` gates visibility until pixel-aligned position is set,
  // so the user never sees the initial (0,0) frame.
  const [placed, setPlaced] = useState(false)
  const [pos, setPos] = useState({ left: 0, top: 0 })
  const wrapperRef = useRef<HTMLElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const hideTimeoutRef = useRef<number | null>(null)
  const showTimeoutRef = useRef<number | null>(null)
  // Stable per-instance identity for the singleton — function identities
  // change on every render so we can't use those.
  const idRef = useRef<object>({})

  const cancelHide = () => {
    if (hideTimeoutRef.current !== null) {
      window.clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }

  const cancelShow = () => {
    if (showTimeoutRef.current !== null) {
      window.clearTimeout(showTimeoutRef.current)
      showTimeoutRef.current = null
    }
  }

  const hideNow = () => {
    cancelShow()
    cancelHide()
    setShow(false)
    setPlaced(false)
    if (activeId === idRef.current) {
      activeId = null
      activeHide = null
    }
  }

  const scheduleHide = () => {
    cancelShow()
    if (!interactive) {
      hideNow()
      return
    }
    cancelHide()
    hideTimeoutRef.current = window.setTimeout(hideNow, TOOLTIP_HIDE_DELAY_MS)
  }

  const reveal = () => {
    showTimeoutRef.current = null
    // Singleton: hide any other open tooltip before showing this one.
    if (activeId && activeId !== idRef.current && activeHide) activeHide()
    activeId = idRef.current
    activeHide = hideNow
    cancelHide()
    setPlaced(false)
    setShow(true)
  }

  const handleEnter = () => {
    if (showDelayMs === 0) {
      reveal()
      return
    }
    cancelShow()
    showTimeoutRef.current = window.setTimeout(reveal, showDelayMs)
  }

  // `mouseover` bubbles (unlike `mouseenter`), so it fires when the cursor
  // moves from a nested tooltip wrapper back into us — the case `mouseenter`
  // misses. We respond only when this wrapper is the innermost tooltip
  // wrapper containing the cursor; nested wrappers handle their own range.
  const handleMouseOver = (event: React.MouseEvent) => {
    if (!wrapperRef.current) return
    const innermost = (event.target as Element).closest("[data-tooltip-wrapper]")
    if (innermost !== wrapperRef.current) return
    if (activeId === idRef.current) return // already shown by us, nothing to do
    if (showTimeoutRef.current !== null) return // mouseover repeats on every child; keep the pending timer
    handleEnter()
  }

  useEffect(() => () => {
    cancelShow()
    cancelHide()
    if (activeId === idRef.current) {
      activeId = null
      activeHide = null
    }
  }, [])

  // Measure trigger + tooltip and snap to integer pixel positions before paint.
  // Sub-pixel left/top values blur the text; useLayoutEffect runs synchronously
  // after DOM mutation so we never render a fractional-pixel frame.
  useLayoutEffect(() => {
    if (!show || !wrapperRef.current || !tooltipRef.current) return
    const trigger = wrapperRef.current.getBoundingClientRect()
    const tooltip = tooltipRef.current.getBoundingClientRect()
    setPos({
      left: Math.round(trigger.left + trigger.width / 2 - tooltip.width / 2),
      top: Math.round(trigger.top - tooltip.height - TOOLTIP_OFFSET_Y),
    })
    setPlaced(true)
  }, [show, content])

  const Wrapper = block ? "div" : "span"

  return (
    <>
      <Wrapper
        ref={wrapperRef as React.RefObject<HTMLDivElement & HTMLSpanElement>}
        data-tooltip-wrapper
        onMouseOver={handleMouseOver}
        onMouseLeave={scheduleHide}
        onFocus={handleEnter}
        onBlur={hideNow}
        onClick={interactive ? undefined : hideNow}
        // For block wrappers we set `width: fit-content` so the hit area
        // matches the inner element's natural width — otherwise the
        // wrapper stretches across its flex parent and the hover zone is
        // wider than the visual affordance (e.g. the narrow divider).
        style={block ? { width: "fit-content" } : { display: "inline-flex" }}
      >
        {children}
      </Wrapper>
      {show &&
        ReactDOM.createPortal(
          <div
            ref={tooltipRef}
            onMouseEnter={cancelHide}
            onMouseLeave={scheduleHide}
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.top,
              visibility: placed ? "visible" : "hidden",
              background: "#424952",
              color: "#FFFFFF",
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
              padding: "8px 12px",
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 500,
              lineHeight: "18px",
              maxWidth: TOOLTIP_MAX_WIDTH,
              width: "max-content",
              textAlign: "left",
              zIndex: 9999,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            }}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  )
}

export default Tooltip
